import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { run } from "../lib/util";

const tones = {
  green: "bg-[#DCFCE7] text-[#166534]",
  amber: "bg-[#FEF3C7] text-[#92400E]",
  red: "bg-[#FEE2E2] text-[#991B1B]",
  info: "bg-[#DBEAFE] text-[#1E40AF]",
  navy: "bg-[#1E3E62] text-white",
  orange: "bg-[#FFF3EB] text-[#C2410C]",
  muted: "bg-slate-100 text-slate-500",
};

export const Badge = ({ tone = "muted", children, ...p }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${tones[tone]}`} {...p}>
    {children}
  </span>
);

export const Pending = () => <Badge tone="amber">Pendente</Badge>;

export const Btn = ({ variant = "primary", className = "", ...p }) => {
  const v = {
    primary: "bg-[#FF6500] hover:bg-[#E05600] text-white shadow-md shadow-orange-500/20",
    navy: "bg-[#1E3E62] hover:bg-[#0B192C] text-white",
    outline: "border border-slate-300 hover:bg-slate-100 text-slate-800 bg-white",
    ghost: "hover:bg-slate-100 text-slate-700",
    light: "border border-white/30 text-white hover:bg-white/10",
    danger: "bg-white border border-red-200 text-red-700 hover:bg-red-50",
  }[variant];
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-[background-color,transform] active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none ${v} ${className}`}
      {...p}
    />
  );
};

export const Card = ({ className = "", children, ...p }) => (
  <div className={`bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 ${className}`} {...p}>
    {children}
  </div>
);

export const Field = ({ label, children, pending, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {label} {pending && <Pending />}
    </span>
    {children}
  </label>
);

export const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6500]/40 focus:border-[#FF6500]";
export const cellCls = "w-full min-w-[70px] rounded-md border border-slate-200 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6500]/40";

export const Input = ({ className = "", ...p }) => <input className={`${inputCls} ${className}`} {...p} />;

export const Select = ({ options, className = "", placeholder, ...p }) => (
  <select className={`${inputCls} ${className}`} {...p}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map(([v, l]) => (
      <option key={v} value={v}>
        {l}
      </option>
    ))}
  </select>
);

export const SectionTitle = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
    <div>
      <h2 className="font-heading text-2xl font-bold text-[#0B192C] tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-3xl">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Table = ({ children, testid }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid={testid}>
    <table className="w-full text-left text-sm text-slate-700 mapia-table">{children}</table>
  </div>
);

export const Empty = ({ children }) => <div className="text-sm text-slate-500 py-8 text-center">{children}</div>;

export const Alert = ({ tone = "amber", title, children, testid }) => (
  <div data-testid={testid} className={`rounded-xl border p-4 text-sm ${tone === "red" ? "border-red-200 bg-red-50 text-red-800" : tone === "info" ? "border-blue-200 bg-blue-50 text-blue-900" : tone === "green" ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
    {title && <div className="font-semibold mb-1">{title}</div>}
    {children}
  </div>
);

export function ProductSelect({ products, value, onChange, testid, allowCreate, createDefaults }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <select
        data-testid={testid}
        className={`${cellCls} ${!value ? "border-amber-400 bg-amber-50" : ""}`}
        value={value || ""}
        onChange={(e) => (e.target.value === "__new" ? setOpen(true) : onChange(e.target.value || null))}
      >
        <option value="">— pendente —</option>
        {(products || []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} ({p.unit})
          </option>
        ))}
        {allowCreate && <option value="__new">+ Cadastrar novo produto…</option>}
      </select>
      {open && <ProductDialog initial={createDefaults} onClose={() => setOpen(false)} onSaved={(p) => onChange(p.id, p)} />}
    </>
  );
}

export function ProductDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", unit: "kg", group: "", ready_made: false, notes: "", ...(initial || {}) });
  const save = async () => {
    const body = { ...f, group: f.group || null, notes: f.notes || null };
    const r = await run(() => (f.id ? api.put(`/s/products/${f.id}`, body) : api.post("/s/products", body)), "Produto salvo");
    if (r) {
      window.dispatchEvent(new Event("mapia:products"));
      onSaved?.(r.data);
      onClose();
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle>{f.id ? "Editar produto" : "Novo produto do estoque"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome" className="col-span-2">
            <Input data-testid="product-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </Field>
          <Field label="Unidade de estoque">
            <Input data-testid="product-unit-input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} />
          </Field>
          <Field label="Grupo alimentar">
            <Input data-testid="product-group-input" value={f.group || ""} placeholder="ex.: proteína animal" onChange={(e) => setF({ ...f, group: e.target.value })} />
          </Field>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="product-ready-checkbox" checked={f.ready_made} onChange={(e) => setF({ ...f, ready_made: e.target.checked })} />
            Produto comprado pronto (não gera consumo de ingredientes do estoque)
          </label>
        </div>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="product-save-button" onClick={save}>Salvar</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Confirm({ title, children, onConfirm, onClose, confirmLabel = "Confirmar", testid = "confirm-dialog" }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-600">{children}</div>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid={`${testid}-ok`} onClick={onConfirm}>{confirmLabel}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
